import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useInventory } from '../hooks/useInventoryOperations';
import { useItems } from '../hooks/useItems';
import { useJobs } from '../hooks/useJobs';
import { useCategories } from '../hooks/useCategories';
import { getQuickDateRanges } from '../utils/dateRangePresets';

const TransactionLedgerPage = () => {
  const [searchParams] = useSearchParams();
  const { getTransactions, getTransactionSummary, exportTransactions, loading } = useInventory();
  const { items } = useItems('', 0, 10000); // Load all items for search
  const { jobs } = useJobs();
  const { categories } = useCategories();
  const [transactions, setTransactions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  // Full-filtered-set credit/debit totals, fetched separately from the
  // paginated list (GET /transactions/summary) so the footer stays correct
  // even when the filtered set spans more than one page. Tracked with its
  // own loading/error state, independent of the table's `loading`, since
  // the two requests run in parallel and can resolve at different times.
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  // CSV export state, tracked separately from the table's `loading` so the
  // export button's own spinner/disabled state can't be affected by, or
  // affect, the list fetch.
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [filters, setFilters] = useState({
    category_id: '',
    item_id: '',
    job_id: searchParams.get('job_id') || '',
    transaction_type: '',
    start_date: '',
    end_date: ''
  });

  // Filter items for search dropdown
  const filteredItems = useMemo(() => {
    let itemsList = items;
    
    // Filter by category if selected
    if (filters.category_id) {
      itemsList = itemsList.filter(item => item.category_id === parseInt(filters.category_id));
    }
    
    // Filter by search term
    if (searchTerm) {
      itemsList = itemsList.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category?.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return itemsList;
  }, [items, filters.category_id, searchTerm]);

  // Quick date-range presets (This Month, Last Month, This Year, Last Year,
  // Q1-Q4), computed once client-side in local time.
  const quickDateRanges = useMemo(() => getQuickDateRanges(), []);
  // Pairs each period with its matching quarter (This Month/Q1, Last
  // Month/Q2, ...) so the two-column grid below reads row-by-row instead of
  // listing all periods before all quarters.
  const quickDateRangeGrid = useMemo(() => {
    const periods = quickDateRanges.slice(0, 4);
    const quarters = quickDateRanges.slice(4);
    return periods.flatMap((period, i) => [period, quarters[i]]);
  }, [quickDateRanges]);

  useEffect(() => {
    fetchTransactions();
  }, [page]);

  // Totals are independent of pagination (they cover the full filtered set),
  // so this only needs to run once on mount; filter-changing actions below
  // trigger their own refetch alongside the list.
  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Shared by the list and summary fetches so filter query-param building
  // can never drift between the two.
  const buildFilterParams = (activeFilters) => {
    const params = {};
    if (activeFilters.category_id) params.category_id = parseInt(activeFilters.category_id);
    if (activeFilters.item_id) params.item_id = parseInt(activeFilters.item_id);
    if (activeFilters.job_id) params.job_id = parseInt(activeFilters.job_id);
    if (activeFilters.transaction_type) params.transaction_type = activeFilters.transaction_type;
    if (activeFilters.start_date) params.start_date = activeFilters.start_date;
    if (activeFilters.end_date) params.end_date = activeFilters.end_date;
    return params;
  };

  // Accepts optional overrides so callers (quick date presets, Apply, Clear)
  // can fetch with filter/page values that haven't finished propagating
  // through React state yet, instead of racing a setTimeout against setState.
  const fetchTransactions = async (overrides = {}) => {
    try {
      const activeFilters = { ...filters, ...overrides };
      const activePage = overrides.page ?? page;
      const params = {
        ...buildFilterParams(activeFilters),
        skip: (activePage - 1) * pageSize,
        limit: pageSize
      };

      const result = await getTransactions(params);
      setTransactions(result.data);
      setTotalCount(result.total);
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
    }
  };

  // Fetches credit/debit totals across the entire filtered set (not just the
  // current page) for the footer. Independent of `fetchTransactions` so a
  // failure here surfaces its own error state without blanking the table.
  const fetchSummary = async (overrides = {}) => {
    const activeFilters = { ...filters, ...overrides };
    const params = buildFilterParams(activeFilters);

    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const result = await getTransactionSummary(params);
      setSummary(result);
    } catch (err) {
      console.error('Failed to fetch transaction summary:', err);
      setSummary(null);
      setSummaryError(err.response?.data?.detail || 'Failed to load totals');
    } finally {
      setSummaryLoading(false);
    }
  };

  // Exports the entire currently-filtered set (not just the visible page) as
  // a CSV file download. Guards against double-triggering a slow request by
  // bailing out if an export is already in flight, and surfaces the
  // backend's row-cap error message (rather than failing silently) when the
  // filtered set is too large to export.
  const handleExportCsv = async () => {
    if (exportLoading) return;

    setExportLoading(true);
    setExportError(null);
    try {
      const params = buildFilterParams(filters);
      const blob = await exportTransactions(params);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'transactions.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export transactions:', err);
      setExportError(err.response?.data?.detail || 'Failed to export transactions');
    } finally {
      setExportLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Sets the start/end date filters from a preset and immediately re-fetches,
  // composing with whatever other filters (job, category, etc.) are already
  // active rather than clearing them. Manually editing the date inputs
  // afterward works normally since there is no separate "preset selected"
  // lock state involved.
  const applyQuickDateRange = (start, end) => {
    setFilters(prev => ({ ...prev, start_date: start, end_date: end }));
    setPage(1);
    fetchTransactions({ start_date: start, end_date: end, page: 1 });
    fetchSummary({ start_date: start, end_date: end });
  };

  const clearFilters = () => {
    const cleared = {
      category_id: '',
      item_id: '',
      job_id: '',
      transaction_type: '',
      start_date: '',
      end_date: ''
    };
    setFilters(cleared);
    setSearchTerm('');
    setPage(1);
    fetchTransactions({ ...cleared, page: 1 });
    fetchSummary(cleared);
  };

  const getTransactionBadge = (type) => {
    return type === 'credit' ? 'bg-success' : 'bg-danger';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const handleApplyFilters = async () => {
    setPage(1); // Reset to first page when applying filters
    fetchTransactions({ page: 1 });
    fetchSummary();
  };

  const handleItemSelect = (item) => {
    handleFilterChange('item_id', item.id);
    setSearchTerm(item.name);
    setShowDropdown(false);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setShowDropdown(true);
    if (e.target.value === '') {
      handleFilterChange('item_id', '');
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    handleFilterChange('item_id', '');
    setShowDropdown(false);
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Transaction Ledger</h2>
        <div>
          <button className="btn btn-outline-secondary me-2" onClick={clearFilters}>
            Clear Filters
          </button>
          <button
            className="btn btn-outline-secondary me-2"
            onClick={handleExportCsv}
            disabled={exportLoading}
            aria-busy={exportLoading}
          >
            {exportLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Exporting...
              </>
            ) : (
              <>
                <i className="bi bi-download me-2"></i>
                Export CSV
              </>
            )}
          </button>
          <button className="btn btn-primary" onClick={() => { fetchTransactions(); fetchSummary(); }}>
            <i className="bi bi-arrow-clockwise me-2"></i>
            Refresh
          </button>
        </div>
      </div>

      {exportError && (
        <div className="alert alert-danger" role="alert">
          {exportError}
        </div>
      )}

      <div className="card mb-4">
        <div className="card-body">
          <h6 className="card-title">Filters</h6>

          <div className="row g-3">
            <div className="col-12 col-lg-6">
              <div className="card h-100 bg-body-tertiary border-0">
                <div className="card-body">
                  <span className="text-uppercase text-muted small fw-semibold">Filter by</span>
                  <div className="row g-3 mt-0">
                    <div className="col-12 col-sm-6">
                      <label className="form-label small" htmlFor="ledger-filter-category">Category</label>
                      <select
                        id="ledger-filter-category"
                        className="form-select form-select-sm"
                        value={filters.category_id}
                        onChange={(e) => {
                          handleFilterChange('category_id', e.target.value);
                          handleFilterChange('item_id', ''); // Reset item when category changes
                        }}
                      >
                        <option value="">All Categories</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12 col-sm-6">
                      <label className="form-label small">Item</label>
                      <div className="position-relative">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Search items..."
                          value={searchTerm}
                          onChange={handleSearchChange}
                          onFocus={() => setShowDropdown(true)}
                        />
                        {searchTerm && (
                          <button
                            className="btn btn-sm position-absolute top-50 end-0 translate-middle-y me-1"
                            onClick={handleClearSearch}
                            style={{ border: 'none', background: 'transparent', padding: '0 0.25rem' }}
                          >
                            <i className="bi bi-x-circle"></i>
                          </button>
                        )}
                        {showDropdown && filteredItems.length > 0 && (
                          <div className="dropdown-menu show w-100" style={{ maxHeight: '300px', overflowY: 'auto', position: 'absolute', zIndex: 1000 }}>
                            {filteredItems.slice(0, 50).map(item => (
                              <button
                                key={item.id}
                                className="dropdown-item"
                                onClick={() => handleItemSelect(item)}
                              >
                                <div>
                                  <strong>{item.name}</strong>
                                  <br />
                                  <small className="text-muted">{item.category?.name}</small>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-12 col-sm-6">
                      <label className="form-label small" htmlFor="ledger-filter-job">Job</label>
                      <select
                        id="ledger-filter-job"
                        className="form-select form-select-sm"
                        value={filters.job_id}
                        onChange={(e) => handleFilterChange('job_id', e.target.value)}
                      >
                        <option value="">All Jobs</option>
                        {jobs.map(job => (
                          <option key={job.id} value={job.id}>{job.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12 col-sm-6">
                      <label className="form-label small" htmlFor="ledger-filter-type">Type</label>
                      <select
                        id="ledger-filter-type"
                        className="form-select form-select-sm"
                        value={filters.transaction_type}
                        onChange={(e) => handleFilterChange('transaction_type', e.target.value)}
                      >
                        <option value="">All Types</option>
                        <option value="credit">Credit (Add)</option>
                        <option value="debit">Debit (Remove)</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-6">
              <div className="row g-3 h-100">
                <div className="col-12 col-md-6">
                  <div className="card h-100 bg-body-tertiary border-0">
                    <div className="card-body">
                      <span className="text-uppercase text-muted small fw-semibold">Date range</span>
                      <div className="mt-2">
                        <label className="form-label small" htmlFor="ledger-filter-start-date">Start Date</label>
                        <input
                          id="ledger-filter-start-date"
                          type="date"
                          className="form-control form-control-sm"
                          value={filters.start_date}
                          onChange={(e) => handleFilterChange('start_date', e.target.value)}
                        />
                      </div>
                      <div className="mt-2">
                        <label className="form-label small" htmlFor="ledger-filter-end-date">End Date</label>
                        <input
                          id="ledger-filter-end-date"
                          type="date"
                          className="form-control form-control-sm"
                          value={filters.end_date}
                          onChange={(e) => handleFilterChange('end_date', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="card h-100 bg-body-tertiary border-0">
                    <div className="card-body">
                      <span className="text-uppercase text-muted small fw-semibold">Quick Date Ranges</span>
                      <div className="row row-cols-2 g-2 mt-0" role="group" aria-label="Quick date ranges">
                        {quickDateRangeGrid.map((range) => (
                          <div className="col" key={range.label}>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary w-100"
                              onClick={() => applyQuickDateRange(range.start, range.end)}
                            >
                              {range.label}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <button className="btn btn-sm btn-primary mt-3" onClick={handleApplyFilters}>
            Apply Filters
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {totalCount > 0 && (
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="text-muted small">
                Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} transactions
              </div>
              <div className="btn-group" role="group">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </button>
                <button className="btn btn-sm btn-outline-secondary" disabled>
                  Page {page} of {totalPages}
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          )}
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover table-sm">
                <thead>
                  <tr>
                    <th>Date & Time</th>
                    <th>Type</th>
                    <th>Item</th>
                    <th>Job</th>
                    <th className="text-end">Quantity</th>
                    <th className="text-end">Unit Price</th>
                    <th className="text-end">Total Value</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center text-muted py-4">
                        No transactions found
                      </td>
                    </tr>
                  ) : (
                    transactions.map(transaction => (
                      <tr key={transaction.id}>
                        <td className="small">{formatDateTime(transaction.created_at)}</td>
                        <td>
                          <span className={`badge ${getTransactionBadge(transaction.transaction_type)}`}>
                            {transaction.transaction_type.toUpperCase()}
                          </span>
                        </td>
                        <td>{transaction.item?.name}</td>
                        <td>
                          {transaction.job ? (
                            <span className="small">{transaction.job.name}</span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td className="text-end">
                          {transaction.transaction_type === 'credit' ? '+' : '-'}
                          {transaction.quantity}
                        </td>
                        <td className="text-end small">{formatCurrency(transaction.unit_price)}</td>
                        <td className="text-end">
                          <strong>{formatCurrency(transaction.total_value)}</strong>
                        </td>
                        <td className="small text-muted">{transaction.notes || '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
          <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
            <div className="text-muted small">
              <span
                title="Net of returns (credits minus debits), scoped to the selected date range. This differs from the Job Cost Summary report, which totals gross debits only, all-time."
              >
                Filtered Total (net of returns, date-scoped)
                <i className="bi bi-info-circle ms-1" aria-hidden="true"></i>
              </span>
            </div>
            <div>
              {summaryError ? (
                <span className="text-danger small" role="alert">
                  Unable to load totals: {summaryError}
                </span>
              ) : (
                <strong data-testid="filtered-total">
                  {formatCurrency(
                    parseFloat(summary?.credit_total ?? 0) - parseFloat(summary?.debit_total ?? 0)
                  )}
                </strong>
              )}
              {summaryLoading && (
                <span className="spinner-border spinner-border-sm text-secondary ms-2" role="status">
                  <span className="visually-hidden">Loading totals...</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionLedgerPage;
